import React, { useMemo, useState } from "react";
import { Alert, Button, Empty, Select, Space, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EyeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";

import { useListAttemptsQuery } from "../../../store/api/gameQuizAttemptsAdminApi";
import { useGetGameQuizzesPaginatedQuery } from "../../../store/api/gameQuizzesApi";
import useDebounce from "../../../hooks/useDebounce";
import {
  ATTEMPT_STATUS_VALUES,
  AttemptListItem,
  AttemptStatus,
  GameQuizKind,
} from "../../../services/gameQuizAttempts/gameQuizAttempts.types";
import AttemptDetailModal from "./AttemptDetailModal";
import { formatScore } from "./formatAnswer";
import { STATUS_META } from "./statusMeta";
import {
  ClassroomFilterSelect,
  fmtDateTime,
  SessionDateRange,
  StudentCell,
  StudentSearchInput,
} from "./SessionFilterControls";
import {
  parsePositiveInt,
  useClampSessionPage,
  useSessionFilters,
} from "./useSessionFilters";

dayjs.extend(relativeTime);
dayjs.locale("es");

const STATUS_CHIP_OPTIONS: { value: AttemptStatus | "ALL"; label: string }[] = [
  { value: "ALL", label: "Todas" },
  { value: "IN_PROGRESS", label: "En curso" },
  { value: "COMPLETED", label: "Completadas" },
  { value: "ABANDONED", label: "Abandonadas" },
];

/** Cómo se nombra el quiz en cada pestaña. */
const KIND_COPY: Record<GameQuizKind, { column: string; filter: string }> = {
  QUIZ: { column: "Quiz", filter: "Filtrar por quiz" },
  VIDEOQUIZ: { column: "Videoquiz", filter: "Filtrar por videoquiz" },
  READING: { column: "Lectura", filter: "Filtrar por lectura" },
};

/** Params que solo tienen sentido en esta pestaña. */
export const QUIZ_TAB_PARAMS = ["status", "gameQuizId"] as const;

const parseStatus = (value: string | null): AttemptStatus | undefined =>
  ATTEMPT_STATUS_VALUES.find((s) => s === value);

/**
 * "hace X" si es <24h, "DD/MM/YYYY HH:mm" en el resto de los casos.
 * "—" si el timestamp no existe o no parsea.
 */
const fmtLastActivity = (iso: string | null | undefined): string => {
  if (!iso || !dayjs(iso).isValid()) return "—";
  const d = dayjs(iso);
  return dayjs().diff(d, "hour") < 24
    ? d.fromNow()
    : d.format("DD/MM/YYYY HH:mm");
};

/**
 * Intentos de quiz de Primaria. Quizzes, videoquizzes y lecturas son el mismo
 * intento; lo que cambia es el recurso del que cuelga el quiz (SCRUM-1167), y
 * cada uno tiene su pestaña.
 */
const QuizAttemptsTab: React.FC<{ kind: GameQuizKind }> = ({ kind }) => {
  const {
    searchParams,
    page,
    pageSize,
    userSearch,
    classroomId,
    from,
    to,
    hasSharedFilters,
    setFilterParams,
    setPage,
  } = useSessionFilters();
  const status = parseStatus(searchParams.get("status"));
  const gameQuizId = parsePositiveInt(searchParams.get("gameQuizId"));
  const copy = KIND_COPY[kind];

  // Estado de detalle: guardamos el id en local (no en la URL) para que el
  // modal sea efímero por operador y no forme parte de un deep link.
  const [detailId, setDetailId] = useState<number | null>(null);

  // Búsqueda de quiz por nombre, contra el backend en vez de precargar un lote
  // fijo (SCRUM-1095), y solo entre los quizzes de esta pestaña.
  const [quizSearch, setQuizSearch] = useState("");
  const debouncedQuizSearch = useDebounce(quizSearch, 300);

  const { data: quizzesData, isFetching: quizzesFetching } =
    useGetGameQuizzesPaginatedQuery({
      page: 1,
      pageSize: 20,
      search: debouncedQuizSearch.trim() || undefined,
      kind,
    });

  // Pre-carga del quiz seleccionado por id para poder mostrar su título en el
  // Select cuando llegás con gameQuizId en la URL sin haber tipeado nada.
  const { data: selectedQuizData } = useGetGameQuizzesPaginatedQuery(
    { page: 1, pageSize: 1, id: gameQuizId },
    { skip: gameQuizId === undefined },
  );

  const quizOptions = useMemo(() => {
    const map = new Map<number, { value: number; label: string }>();
    (quizzesData?.data ?? []).forEach((q) => {
      map.set(q.id, { value: q.id, label: q.title });
    });
    if (gameQuizId !== undefined) {
      const selected = selectedQuizData?.data.find((q) => q.id === gameQuizId);
      if (selected && !map.has(selected.id)) {
        map.set(selected.id, { value: selected.id, label: selected.title });
      }
    }
    return Array.from(map.values());
  }, [quizzesData, selectedQuizData, gameQuizId]);

  const { data, isFetching, isError, refetch } = useListAttemptsQuery({
    page,
    pageSize,
    kind,
    ...(status && { status }),
    ...(userSearch && { userSearch }),
    ...(gameQuizId !== undefined && { gameQuizId }),
    ...(classroomId !== undefined && { classroomId }),
    ...(from && { from }),
    ...(to && { to }),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  useClampSessionPage(total, page, pageSize, setPage);

  const handleClearFilters = () => {
    setQuizSearch("");
    setFilterParams({
      status: undefined,
      userSearch: undefined,
      gameQuizId: undefined,
      classroomId: undefined,
      from: undefined,
      to: undefined,
    });
  };

  const hasActiveFilters =
    hasSharedFilters || Boolean(status) || gameQuizId !== undefined;

  const columns: ColumnsType<AttemptListItem> = [
    {
      title: "Usuario",
      key: "user",
      render: (_: unknown, record) => <StudentCell user={record.user} />,
    },
    {
      title: copy.column,
      key: "quiz",
      render: (_: unknown, record) => record.game_quiz.title,
    },
    {
      title: "Estado",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (value: AttemptStatus) => {
        const meta = STATUS_META[value];
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: "Progreso",
      key: "progress",
      width: 110,
      render: (_: unknown, record) => {
        if (
          record.current_question === null ||
          record.total_count === null ||
          record.total_count === undefined
        ) {
          return "—";
        }
        return `${record.current_question + 1} / ${record.total_count}`;
      },
    },
    {
      title: "Score",
      key: "score",
      width: 110,
      render: (_: unknown, record) => {
        if (
          record.score === null ||
          record.total_points === null ||
          record.total_points === undefined
        ) {
          return "—";
        }
        return formatScore(record.score, record.total_points);
      },
    },
    {
      // Varias filas del mismo alumno para el mismo quiz son intentos
      // sucesivos, no duplicados. Sin este número la tabla no deja distinguir
      // el primer intento del cuarto.
      title: "Intento",
      key: "attempt_number",
      width: 90,
      align: "center" as const,
      render: (_: unknown, record) =>
        record.attempt_number === null || record.attempt_number === undefined
          ? "—"
          : `#${record.attempt_number}`,
    },
    {
      title: "Iniciado",
      dataIndex: "started_at",
      key: "started_at",
      width: 160,
      render: (iso: string) => fmtDateTime(iso),
    },
    {
      title: "Última actividad",
      dataIndex: "last_saved_at",
      key: "last_saved_at",
      width: 170,
      render: (iso: string) => (
        <Tooltip title={fmtDateTime(iso)}>{fmtLastActivity(iso)}</Tooltip>
      ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 100,
      render: (_: unknown, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          aria-label="Ver detalle"
          onClick={() => setDetailId(record.id)}
        />
      ),
    },
  ];

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      {/* Chip group para status. */}
      <Space wrap>
        {STATUS_CHIP_OPTIONS.map((opt) => {
          const active =
            (opt.value === "ALL" && !status) || opt.value === status;
          return (
            <Tag.CheckableTag
              key={opt.value}
              checked={active}
              onChange={() =>
                setFilterParams({
                  status: opt.value === "ALL" ? undefined : opt.value,
                })
              }
            >
              {opt.label}
            </Tag.CheckableTag>
          );
        })}
      </Space>

      <Space wrap>
        <StudentSearchInput
          value={userSearch}
          onCommit={(value) => setFilterParams({ userSearch: value })}
        />

        <ClassroomFilterSelect
          value={classroomId}
          onChange={(value) =>
            setFilterParams({
              classroomId: value !== undefined ? String(value) : undefined,
            })
          }
        />

        <Select<number | undefined>
          allowClear
          showSearch
          placeholder={copy.filter}
          style={{ width: 260 }}
          value={gameQuizId}
          onChange={(value) =>
            setFilterParams({
              gameQuizId: value !== undefined ? String(value) : undefined,
            })
          }
          onSearch={setQuizSearch}
          filterOption={false}
          loading={quizzesFetching}
          notFoundContent={quizzesFetching ? "Buscando…" : "Sin coincidencias"}
          options={quizOptions}
        />

        <SessionDateRange from={from} to={to} onChange={setFilterParams} />

        {hasActiveFilters && (
          <Button onClick={handleClearFilters}>Limpiar filtros</Button>
        )}
      </Space>

      {isError && (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar las sesiones."
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      )}

      <Table<AttemptListItem>
        size="small"
        columns={columns}
        dataSource={items}
        rowKey="id"
        loading={isFetching}
        locale={{
          emptyText: (
            <Empty description="No hay sesiones para los filtros elegidos.">
              {hasActiveFilters && (
                <Button onClick={handleClearFilters}>Limpiar filtros</Button>
              )}
            </Empty>
          ),
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onChange: setPage,
        }}
      />

      <AttemptDetailModal
        attemptId={detailId}
        open={detailId !== null}
        onClose={() => setDetailId(null)}
      />
    </Space>
  );
};

export default QuizAttemptsTab;
